import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import { assessmentUpdateNotifier } from '../utils/assessmentUpdateNotifier';

interface AssessmentResponse {
  questionId: string;
  answer: number;
  timestamp: Date;
}

interface ReligioTraditioCulturalData {
  responses: AssessmentResponse[];
  scores: {
    overall: number;
    category1: number; // Brief RCOPE
    category2: number; // CVSCALE
    category3: number; // TVAS
  };
  results: {
    level: 'Low' | 'Moderate' | 'High';
    interpretation: string;
    recommendations: string[];
  };
}

const ReligioTraditioCulturalAssessmentFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate total questions based on selected category
  const getTotalQuestions = () => {
    if (!selectedCategory) {
      return 1; // Just the category selection question
    }
    if (selectedCategory === 1) {
      return 14; // Brief RCOPE has 14 questions
    }
    if (selectedCategory === 2) {
      return 26; // CVSCALE has 26 questions
    }
    if (selectedCategory === 3) {
      return 10; // TVAS has 10 questions
    }
    return 5;
  };
  
  const totalQuestions = getTotalQuestions();

  // Category selection question
  const categorySelectionQuestion = {
    id: '0',
    question: 'Which Religio-Traditio-Cultural assessment would you like to take?',
    type: 'selection' as const,
    options: [
      'Brief RCOPE (Religious Coping)',
      'CVSCALE (Cultural Value Scale)',
      'TVAS (Traditional Values Assessment Scale)'
    ]
  };

  // Assessment questions organized by category
  const categoryQuestions: { [key: number]: Array<{ id: string; question: string; category: number; type: string }> } = {
    1: [
      // Brief RCOPE - 14 items (Positive Coping: 1-7, Negative Coping: 8-14)
      { id: '1', question: 'Sought God\'s love and care.', category: 1, type: 'likert' },
      { id: '2', question: 'Sought help from God in letting go of my anger.', category: 1, type: 'likert' },
      { id: '3', question: 'Tried to put my plans into action together with God.', category: 1, type: 'likert' },
      { id: '4', question: 'Looked for a stronger connection with God.', category: 1, type: 'likert' },
      { id: '5', question: 'Tried to see how God might be strengthening me in this situation.', category: 1, type: 'likert' },
      { id: '6', question: 'Asked forgiveness for my sins.', category: 1, type: 'likert' },
      { id: '7', question: 'Focused on religion to stop worrying about my problems.', category: 1, type: 'likert' },
      { id: '8', question: 'Wondered whether God had abandoned me.', category: 1, type: 'likert' },
      { id: '9', question: 'Felt punished by God for my lack of devotion.', category: 1, type: 'likert' },
      { id: '10', question: 'Wondered what I did for God to punish me.', category: 1, type: 'likert' },
      { id: '11', question: 'Questioned God\'s love for me.', category: 1, type: 'likert' },
      { id: '12', question: 'Wondered whether my church/spiritual community had abandoned me.', category: 1, type: 'likert' },
      { id: '13', question: 'Decided the devil made this happen.', category: 1, type: 'likert' },
      { id: '14', question: 'Questioned the power of God.', category: 1, type: 'likert' }
    ],
    2: [
      // CVSCALE (Cultural Values Scale) - 26 items
      // Collectivism (6 items)
      { id: '1', question: 'Group welfare is more important than individual rewards.', category: 2, type: 'likert' },
      { id: '2', question: 'Group success is more important than individual success.', category: 2, type: 'likert' },
      { id: '3', question: 'Individuals should sacrifice self-interest for the group.', category: 2, type: 'likert' },
      { id: '4', question: 'Group loyalty should be encouraged even if individual goals suffer.', category: 2, type: 'likert' },
      { id: '5', question: 'Being accepted as a member of a group is more important than being independent.', category: 2, type: 'likert' },
      { id: '6', question: 'Group harmony is more important than personal achievement.', category: 2, type: 'likert' },
      // Power Distance (6 items)
      { id: '7', question: 'People in higher positions should make decisions without consulting people in lower positions.', category: 2, type: 'likert' },
      { id: '8', question: 'People in higher positions should not ask the opinions of people in lower positions too frequently.', category: 2, type: 'likert' },
      { id: '9', question: 'People in higher positions should avoid social interaction with people in lower positions.', category: 2, type: 'likert' },
      { id: '10', question: 'People in higher positions should be treated with more respect.', category: 2, type: 'likert' },
      { id: '11', question: 'People in higher positions should enjoy privileges that people in lower positions do not have.', category: 2, type: 'likert' },
      { id: '12', question: 'People in lower positions should not disagree with decisions made by people in higher positions.', category: 2, type: 'likert' },
      // Uncertainty Avoidance (5 items)
      { id: '13', question: 'Rules and regulations are important because they inform me of what is expected.', category: 2, type: 'likert' },
      { id: '14', question: 'It is important to closely follow instructions and procedures.', category: 2, type: 'likert' },
      { id: '15', question: 'Standardized work procedures are helpful.', category: 2, type: 'likert' },
      { id: '16', question: 'Instructions for operations are important.', category: 2, type: 'likert' },
      { id: '17', question: 'Rules and regulations should not be broken, even when it is in the individual\'s best interest.', category: 2, type: 'likert' },
      // Masculinity vs Femininity (5 items)
      { id: '18', question: 'It is more important for men to have a professional career than it is for women.', category: 2, type: 'likert' },
      { id: '19', question: 'Men usually solve problems with logical analysis; women usually solve problems with intuition.', category: 2, type: 'likert' },
      { id: '20', question: 'Solving organizational problems requires a man\'s touch.', category: 2, type: 'likert' },
      { id: '21', question: 'It is more important for men to be respected at work than it is for women.', category: 2, type: 'likert' },
      { id: '22', question: 'Work is more important for men than it is for women.', category: 2, type: 'likert' },
      // Long-Term Orientation (4 items)
      { id: '23', question: 'More importance should be placed on working hard for future rewards than on short-term success.', category: 2, type: 'likert' },
      { id: '24', question: 'A long-term planning horizon is more important than a short-term focus.', category: 2, type: 'likert' },
      { id: '25', question: 'Working toward future goals is more important than immediate needs.', category: 2, type: 'likert' },
      { id: '26', question: 'I believe in sacrificing today to achieve long-term success.', category: 2, type: 'likert' }
    ],
    3: [
      // TVAS (Traditional Values Assessment Scale) - 10 items
      // Respect for Tradition (3 items)
      { id: '1', question: 'It is important for me to respect and follow the customs handed down by my family.', category: 3, type: 'likert' },
      { id: '2', question: 'I feel that honoring the traditions of my community contributes to my well-being.', category: 3, type: 'likert' },
      { id: '3', question: 'I believe traditions provide guidance for making good life decisions.', category: 3, type: 'likert' },
      // Family & Community Values (3 items)
      { id: '4', question: 'I prioritize family obligations over my own individual needs.', category: 3, type: 'likert' },
      { id: '5', question: 'Respecting elders is essential for maintaining harmony and balance in life.', category: 3, type: 'likert' },
      { id: '6', question: 'Being recognized as a good family/community member is more important to me than personal achievement.', category: 3, type: 'likert' },
      // Identity & Continuity (2 items)
      { id: '7', question: 'I feel a strong connection to the traditions of my ancestors.', category: 3, type: 'likert' },
      { id: '8', question: 'Passing on traditions (stories, rituals, language, practices) to the next generation is important for me.', category: 3, type: 'likert' },
      // Coping & Resilience (2 items)
      { id: '9', question: 'When facing difficulties, I find strength in traditional practices or beliefs.', category: 3, type: 'likert' },
      { id: '10', question: 'I believe traditional values help me maintain emotional balance and resilience.', category: 3, type: 'likert' }
    ]
  };

  // Get current questions based on selected category
  const getCurrentQuestions = () => {
    if (!selectedCategory) {
      return [categorySelectionQuestion];
    }
    // When category is selected, only return category-specific questions
    return categoryQuestions[selectedCategory as keyof typeof categoryQuestions] || [];
  };

  const questions = getCurrentQuestions();

  // Get answer options based on selected category
  const getAnswerOptions = () => {
    if (selectedCategory === 1) {
      // Brief RCOPE uses 1-4 scale
      return [
        { value: 1, label: 'Not at all' },
        { value: 2, label: 'Somewhat' },
        { value: 3, label: 'Quite a bit' },
        { value: 4, label: 'A great deal' }
      ];
    } else if (selectedCategory === 2) {
      // CVSCALE uses 1-5 scale
      return [
        { value: 1, label: 'Strongly disagree' },
        { value: 2, label: 'Disagree' },
        { value: 3, label: 'Neutral' },
        { value: 4, label: 'Agree' },
        { value: 5, label: 'Strongly agree' }
      ];
    } else if (selectedCategory === 3) {
      // TVAS uses 1-5 scale
      return [
        { value: 1, label: 'Strongly disagree' },
        { value: 2, label: 'Disagree' },
        { value: 3, label: 'Neutral' },
        { value: 4, label: 'Agree' },
        { value: 5, label: 'Strongly agree' }
      ];
    }
    return [];
  };

  const answerOptions = getAnswerOptions();

  const currentQuestionData = questions[currentQuestion - 1];
  const currentResponse = currentQuestionData ? responses.find(r => r.questionId === currentQuestionData.id) : null;
  const progress = (currentQuestion / totalQuestions) * 100;

  // Type guard to check if question has options (selection type)
  const isSelectionQuestion = (question: any): question is { id: string; question: string; type: 'selection'; options: string[] } => {
    return question.type === 'selection' && 'options' in question;
  };

  // Type guard to check if question has category (likert type)
  const isLikertQuestion = (question: any): question is { id: string; question: string; category: number; type: string } => {
    return question.type === 'likert' && 'category' in question;
  };

  const getCategoryName = (categoryNumber: number) => {
    switch (categoryNumber) {
      case 1: return 'Brief RCOPE (Religious Coping)';
      case 2: return 'CVSCALE (Cultural Value Scale)';
      case 3: return 'TVAS (Traditional Values Assessment Scale)';
      default: return 'Unknown Category';
    }
  };

  const saveCurrentResponse = (answer: number) => {
    if (!currentQuestionData) return;
    
    const newResponse: AssessmentResponse = {
      questionId: currentQuestionData.id,
      answer,
      timestamp: new Date()
    };

    setResponses(prev => {
      const filtered = prev.filter(r => r.questionId !== currentQuestionData.id);
      return [...filtered, newResponse];
    });

    // If this is the category selection question, set the selected category
    if (currentQuestionData.id === '0') {
      const categoryMap = [1, 2, 3]; // Map selection index to category number
      setSelectedCategory(categoryMap[answer]);
      // Reset to question 1 for the selected category
      setCurrentQuestion(1);
    }
  };

  const handleNext = () => {
    if (currentQuestion < totalQuestions) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentQuestion > 1) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const calculateScores = (responses: AssessmentResponse[], selectedCategory: number) => {
    // Filter out the category selection response (id: '0')
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    
    if (selectedCategory === 1) {
      // Brief RCOPE scoring
      // Positive Religious Coping (PRC): Items 1-7, range 7-28
      const prcResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 1 && questionNumber <= 7;
      });
      const prcTotal = prcResponses.reduce((sum, r) => sum + r.answer, 0);
      
      // Negative Religious Coping (NRC): Items 8-14, range 7-28 (no reverse scoring)
      const nrcResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 8 && questionNumber <= 14;
      });

      // Sum raw scores for NRC (higher scores = more negative coping)
      const nrcTotal = nrcResponses.reduce((sum, r) => sum + Number(r.answer), 0);
      
      const overallTotal = prcTotal + nrcTotal; // Total range: 14-56
      
      return {
        overall: overallTotal,
        category1: overallTotal,
        category2: 0,
        category3: 0,
        briefRcopeTotal: overallTotal,
        prcTotal: prcTotal,
        nrcTotal: nrcTotal
      };
    } else if (selectedCategory === 2) {
      // CVSCALE scoring - 5 dimensions
      // Collectivism: Items 1-6
      const collectivismResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 1 && questionNumber <= 6;
      });
      const collectivismTotal = collectivismResponses.reduce((sum, r) => sum + r.answer, 0);
      const collectivismMean = collectivismTotal / 6;
      
      // Power Distance: Items 7-12
      const powerDistanceResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 7 && questionNumber <= 12;
      });
      const powerDistanceTotal = powerDistanceResponses.reduce((sum, r) => sum + r.answer, 0);
      const powerDistanceMean = powerDistanceTotal / 6;
      
      // Uncertainty Avoidance: Items 13-17
      const uncertaintyAvoidanceResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 13 && questionNumber <= 17;
      });
      const uncertaintyAvoidanceTotal = uncertaintyAvoidanceResponses.reduce((sum, r) => sum + r.answer, 0);
      const uncertaintyAvoidanceMean = uncertaintyAvoidanceTotal / 5;
      
      // Masculinity: Items 18-22
      const masculinityResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 18 && questionNumber <= 22;
      });
      const masculinityTotal = masculinityResponses.reduce((sum, r) => sum + r.answer, 0);
      const masculinityMean = masculinityTotal / 5;
      
      // Long-Term Orientation: Items 23-26
      const longTermOrientationResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 23 && questionNumber <= 26;
      });
      const longTermOrientationTotal = longTermOrientationResponses.reduce((sum, r) => sum + r.answer, 0);
      const longTermOrientationMean = longTermOrientationTotal / 4;
      
      // Overall cultural values index = average of all items
      const overallTotal = assessmentResponses.reduce((sum, r) => sum + r.answer, 0);
      const overallMean = overallTotal / 26;
      
      return {
        overall: Math.round(overallMean * 100) / 100, // Round to 2 decimal places
        category1: 0,
        category2: Math.round(overallMean * 100) / 100,
        category3: 0,
        cvscaleTotal: Math.round(overallMean * 100) / 100,
        collectivismMean: Math.round(collectivismMean * 100) / 100,
        powerDistanceMean: Math.round(powerDistanceMean * 100) / 100,
        uncertaintyAvoidanceMean: Math.round(uncertaintyAvoidanceMean * 100) / 100,
        masculinityMean: Math.round(masculinityMean * 100) / 100,
        longTermOrientationMean: Math.round(longTermOrientationMean * 100) / 100
      };
    } else if (selectedCategory === 3) {
      // TVAS scoring - 4 subscales
      // Respect for Tradition: Items 1-3
      const respectForTraditionResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 1 && questionNumber <= 3;
      });
      const respectForTraditionTotal = respectForTraditionResponses.reduce((sum, r) => sum + r.answer, 0);
      
      // Family & Community Values: Items 4-6
      const familyCommunityResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 4 && questionNumber <= 6;
      });
      const familyCommunityTotal = familyCommunityResponses.reduce((sum, r) => sum + r.answer, 0);
      
      // Identity & Continuity: Items 7-8
      const identityContinuityResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 7 && questionNumber <= 8;
      });
      const identityContinuityTotal = identityContinuityResponses.reduce((sum, r) => sum + r.answer, 0);
      
      // Coping & Resilience: Items 9-10
      const copingResilienceResponses = assessmentResponses.filter(r => {
        const questionNumber = parseInt(r.questionId);
        return questionNumber >= 9 && questionNumber <= 10;
      });
      const copingResilienceTotal = copingResilienceResponses.reduce((sum, r) => sum + r.answer, 0);
      
      // Overall TVAS total (range: 10-50)
      const tvasTotal = assessmentResponses.reduce((sum, r) => sum + r.answer, 0);
      
      return {
        overall: tvasTotal,
        category1: 0,
        category2: 0,
        category3: tvasTotal,
        tvasTotal: tvasTotal,
        respectForTraditionTotal: respectForTraditionTotal,
        familyCommunityTotal: familyCommunityTotal,
        identityContinuityTotal: identityContinuityTotal,
        copingResilienceTotal: copingResilienceTotal
      };
    } else {
      return {
        overall: 0,
        category1: 0,
        category2: 0,
        category3: 0
      };
    }
  };

  const interpretResults = (scores: any, selectedCategory: number) => {
    if (selectedCategory === 1) {
      // Brief RCOPE interpretation
      const { prcTotal, nrcTotal } = scores;

      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      // Determine overall level based on PRC vs NRC comparison
      if (prcTotal > nrcTotal + 7) {
        // Strong Positive Coping dominance
        level = 'High';
        interpretation = `Strong Positive Religious Coping. You demonstrate high resilience through religious coping with strong positive coping strategies and minimal negative coping patterns.`;
        recommendations = [
          'Continue nurturing your positive religious coping strategies',
          'Share your faith-based coping methods with others who may benefit',
          'Maintain regular spiritual practices that strengthen your connection with God',
          'Consider how your faith can continue to support you in future challenges'
        ];
      } else if (prcTotal > nrcTotal) {
        // Moderate Positive Coping
        level = 'Moderate';
        interpretation = `Balanced Religious Coping with Positive Orientation. You generally turn to faith in adaptive ways, though you may occasionally experience some spiritual struggle.`;
        recommendations = [
          'Strengthen your positive religious coping practices through regular prayer and spiritual reflection',
          'Address any spiritual struggles by discussing them with a trusted spiritual advisor or counselor',
          'Engage with your religious or spiritual community for support',
          'Practice gratitude and focus on how your faith provides comfort and strength'
        ];
      } else if (nrcTotal > prcTotal + 7) {
        // Strong Negative Coping dominance - Spiritual Struggle
        level = 'Low';
        interpretation = `Significant Spiritual Struggle. You may be experiencing considerable spiritual distress or questioning your faith during difficulties.`;
        recommendations = [
          'Seek support from a pastoral counselor or mental health professional trained in spiritual issues',
          'Consider discussing your spiritual struggles with a trusted religious leader',
          'Explore the roots of your spiritual distress in a safe, supportive environment',
          'Practice self-compassion and remember that spiritual struggles are common during difficult times',
          'Consider joining a support group where you can share your experiences',
          'Focus on small, positive spiritual practices that feel manageable'
        ];
      } else {
        // Mixed or balanced
        level = 'Moderate';
        interpretation = `Mixed Religious Coping Pattern. You use both positive and negative religious coping strategies when facing difficulties.`;
        recommendations = [
          'Work on strengthening positive religious coping while addressing spiritual struggles',
          'Consider speaking with a pastoral counselor to explore your spiritual experiences',
          'Engage in spiritual practices that bring comfort and connection',
          'Join a faith community or support group to share experiences and gain perspective',
          'Practice mindfulness and reflection to understand your spiritual responses to stress'
        ];
      }

      return { level, interpretation, recommendations };
    } else if (selectedCategory === 2) {
      // CVSCALE interpretation
      const { collectivismMean, powerDistanceMean, uncertaintyAvoidanceMean, masculinityMean, longTermOrientationMean, cvscaleTotal } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      // Determine overall level based on mean score
      if (cvscaleTotal >= 3.5) {
        level = 'High';
      } else if (cvscaleTotal >= 2.5) {
        level = 'Moderate';
      } else {
        level = 'Low';
      }
      
      // Helper function to categorize dimension scores
      const categorizeDimension = (score: number) => {
        if (score >= 3.5) return 'High';
        if (score >= 2.5) return 'Moderate';
        return 'Low';
      };
      
      // Build interpretation
      interpretation = `Cultural Values Profile (Overall Score: ${cvscaleTotal}/5.0)\n\n`;
      interpretation += `Your cultural values assessment reveals the following dimensions:\n\n`;
      interpretation += `• Collectivism (${collectivismMean}/5.0 - ${categorizeDimension(collectivismMean)}): `;
      if (collectivismMean >= 3.5) {
        interpretation += 'You strongly prioritize group harmony and welfare over individual goals. You value family and community connections and tend to make health decisions with collective well-being in mind.';
      } else if (collectivismMean >= 2.5) {
        interpretation += 'You balance group needs with individual goals. You value both personal independence and group harmony.';
      } else {
        interpretation += 'You tend to prioritize individual goals and independence over group conformity.';
      }
      
      interpretation += `\n\n• Power Distance (${powerDistanceMean}/5.0 - ${categorizeDimension(powerDistanceMean)}): `;
      if (powerDistanceMean >= 3.5) {
        interpretation += 'You accept and respect hierarchical structures and authority. You believe in clear organizational roles and deference to those in higher positions.';
      } else if (powerDistanceMean >= 2.5) {
        interpretation += 'You have a balanced view of hierarchy. You respect authority but also value input from all levels.';
      } else {
        interpretation += 'You prefer egalitarian relationships and believe in minimizing power differences between people.';
      }
      
      interpretation += `\n\n• Uncertainty Avoidance (${uncertaintyAvoidanceMean}/5.0 - ${categorizeDimension(uncertaintyAvoidanceMean)}): `;
      if (uncertaintyAvoidanceMean >= 3.5) {
        interpretation += 'You strongly prefer order, structure, and clear rules. You value stability and are likely to follow established health guidelines and preventive care routines.';
      } else if (uncertaintyAvoidanceMean >= 2.5) {
        interpretation += 'You balance the need for structure with flexibility. You can adapt to change while also appreciating clear guidelines.';
      } else {
        interpretation += 'You are comfortable with ambiguity and change. You prefer flexibility over rigid rules.';
      }
      
      interpretation += `\n\n• Masculinity vs Femininity (${masculinityMean}/5.0 - ${categorizeDimension(masculinityMean)}): `;
      if (masculinityMean >= 3.5) {
        interpretation += 'You tend to value competition, achievement, and traditional gender roles. You may emphasize career success and assertiveness.';
      } else if (masculinityMean >= 2.5) {
        interpretation += 'You balance achievement with collaboration and care for others, valuing both competition and cooperation.';
      } else {
        interpretation += 'You strongly value collaboration, caring for others, and quality of life. You tend to reject rigid gender roles and emphasize work-life balance.';
      }
      
      interpretation += `\n\n• Long-Term Orientation (${longTermOrientationMean}/5.0 - ${categorizeDimension(longTermOrientationMean)}): `;
      if (longTermOrientationMean >= 3.5) {
        interpretation += 'You are highly future-focused and value perseverance and long-term planning. You are likely to invest in preventive health behaviors and long-term wellness strategies.';
      } else if (longTermOrientationMean >= 2.5) {
        interpretation += 'You balance short-term needs with long-term goals. You plan for the future while attending to immediate concerns.';
      } else {
        interpretation += 'You tend to focus more on present needs and short-term outcomes than long-term planning.';
      }
      
      // Generate recommendations based on profile
      recommendations = [
        'Reflect on how your cultural values influence your health and wellness decisions',
        'Consider how your cultural background shapes your relationships and community connections'
      ];
      
      if (collectivismMean >= 3.5) {
        recommendations.push('Leverage your strong family and community ties for health support and wellness activities');
      }
      
      if (longTermOrientationMean >= 3.5) {
        recommendations.push('Continue your focus on preventive care and long-term wellness planning');
      }
      
      if (uncertaintyAvoidanceMean >= 3.5) {
        recommendations.push('Use your preference for structure to establish consistent health routines');
      }
      
      if (powerDistanceMean >= 3.5) {
        recommendations.push('Work with healthcare providers in a way that respects your comfort with hierarchical relationships');
      }
      
      recommendations.push('Be aware of how cultural values may influence your approach to stress, health decisions, and well-being');
      
      return { level, interpretation, recommendations };
    } else if (selectedCategory === 3) {
      // TVAS interpretation
      const { tvasTotal, respectForTraditionTotal, familyCommunityTotal, identityContinuityTotal, copingResilienceTotal } = scores;
      
      let level: 'Low' | 'Moderate' | 'High';
      let interpretation: string;
      let recommendations: string[] = [];
      
      // Determine overall level based on total score
      if (tvasTotal >= 38) {
        level = 'High';
        interpretation = `High Traditional Value Orientation (Score: ${tvasTotal}/50). Tradition strongly shapes your lifestyle, coping strategies, and sense of meaning. You have a deep respect for cultural customs (${respectForTraditionTotal}/15), strong family and community values (${familyCommunityTotal}/15), a powerful connection to your ancestral heritage (${identityContinuityTotal}/10), and find significant strength in traditional practices (${copingResilienceTotal}/10). This orientation provides protective benefits for your well-being through family ties, resilience, and social harmony. Your strong traditional values give you a clear sense of identity and purpose, though it's important to maintain balance between tradition and personal autonomy.`;
        recommendations = [
          'Continue honoring and practicing the traditions that give your life meaning',
          'Share your cultural heritage and traditional knowledge with younger generations',
          'Use your strong family and community connections as sources of support during difficult times',
          'Participate actively in cultural and community events that reinforce your values',
          'Balance respect for tradition with openness to beneficial modern practices',
          'Serve as a cultural bridge, helping others appreciate traditional values'
        ];
      } else if (tvasTotal >= 25) {
        level = 'Moderate';
        interpretation = `Moderate Traditional Value Orientation (Score: ${tvasTotal}/50). You have a balanced approach, drawing on tradition for identity and support (Respect for Tradition: ${respectForTraditionTotal}/15, Family & Community: ${familyCommunityTotal}/15, Identity & Continuity: ${identityContinuityTotal}/10, Coping & Resilience: ${copingResilienceTotal}/10) while also embracing modern perspectives. You appreciate your cultural heritage and family obligations, but also value personal goals and contemporary approaches. This balance allows you to benefit from traditional wisdom while remaining adaptable to change.`;
        recommendations = [
          'Reflect on which traditional values are most meaningful to you and why',
          'Find ways to integrate valued traditions into your modern lifestyle',
          'Strengthen connections with family and community while pursuing personal goals',
          'Explore how traditional practices can support your current well-being needs',
          'Share selective traditions with the next generation',
          'Seek balance between honoring cultural expectations and personal autonomy'
        ];
      } else {
        level = 'Low';
        interpretation = `Low Traditional Value Orientation (Score: ${tvasTotal}/50). Tradition has limited influence on your lifestyle and well-being approach. Your scores suggest lower emphasis on traditional customs (${respectForTraditionTotal}/15), family obligations over individual needs (${familyCommunityTotal}/15), ancestral connections (${identityContinuityTotal}/10), and traditional coping methods (${copingResilienceTotal}/10). Your well-being is primarily guided by modern, individualistic values rather than traditional collective values. While this provides greater personal freedom, you may experience some vulnerability to stress from lack of cultural anchoring or family support systems.`;
        recommendations = [
          'Consider exploring your cultural heritage to discover potentially meaningful traditions',
          'Reflect on whether reconnecting with family or community traditions might provide additional support',
          'Find balance between individual autonomy and beneficial cultural practices',
          'Explore how traditional values might complement your modern lifestyle',
          'Build strong social support networks to compensate for less traditional family ties',
          'Consider the protective role of community and cultural connection for overall well-being',
          'Be mindful of potential isolation that can come from purely individualistic approaches'
        ];
      }
      
      return { level, interpretation, recommendations };
    } else {
      // Placeholder for other categories
      let level: 'Low' | 'Moderate' | 'High' = 'Moderate';
      let interpretation = 'Assessment completed. Detailed interpretation will be available once questions are configured.';
      let recommendations: string[] = ['Continue exploring your religious, traditional, and cultural values.'];

      return { level, interpretation, recommendations };
    }
  };

  const handleSubmit = async () => {
    // Filter out the category selection response for validation
    const assessmentResponses = responses.filter(r => r.questionId !== '0');
    if (assessmentResponses.length < totalQuestions) {
      alert('Please answer all questions before submitting.');
      return;
    }

    setIsSubmitting(true);

    try {
      const scores = calculateScores(responses, selectedCategory!);
      const results = interpretResults(scores, selectedCategory!);

      const assessmentData: ReligioTraditioCulturalData = {
        responses,
        scores,
        results
      };

      const response = await fetch(API_CONFIG.ENDPOINTS.RELIGIO_TRADITIO_CULTURAL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include',
        body: JSON.stringify(assessmentData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('SESSION_EXPIRED');
        }
        throw new Error('Failed to submit assessment');
      }

      const result = await response.json();
      
      // Clear Insights page cache to force refresh
      const userId = JSON.parse(localStorage.getItem('user') || '{}').id;
      const insightsCacheKey = userId ? `lifepsensor_insights_cache_${userId}` : 'lifepsensor_insights_cache';
      localStorage.removeItem(insightsCacheKey);
      
      // Notify InsightsPage about the update
      assessmentUpdateNotifier.notifyUpdate('religio-traditio-cultural');
      
      // Navigate to completion page
      navigate('/panel/learn/religio-traditio-cultural-assessment/completion', {
        state: { assessmentResult: result.data }
      });

    } catch (error) {

      let errorMessage = 'Error submitting assessment. Please try again.';
      if (error instanceof Error && error.message === 'SESSION_EXPIRED') {
        errorMessage = 'Your session has expired. Please log in again to continue.';
      }
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Religio-Traditio-Cultural Assessment</h1>
              <p>Processing your responses...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if currentQuestionData exists
  if (!currentQuestionData) {
    return (
      <div className="assessment-form-page">
        <div className="assessment-container">
          <div className="assessment-left-panel">
            <div className="assessment-header">
              <h1>Religio-Traditio-Cultural Assessment</h1>
              <p>Loading assessment questions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="assessment-form-page">
      <div className="assessment-container">
        {/* Left Panel - Background Image */}
        <div
          className="assessment-left-panel religio-traditional-cultural-panel"
        >
          <div className="assessment-header">
            <h1>Religio-Traditio-Cultural Assessment</h1>
            <p className="assessment-subtitle">
              Exploring your religious, traditional, and cultural values
            </p>
            <p className="assessment-tool-info">
              Comprehensive assessment of religious coping, cultural values, and traditional beliefs
            </p>
          </div>
        </div>

        {/* Right Panel - Questions */}
        <div className="assessment-right-panel">
          <div className="assessment-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${progress}%` }}></div>
            </div>
            <span className="progress-text">
              Question {currentQuestion} of {totalQuestions}
            </span>
          </div>

          <div className="question-section">
            {isSelectionQuestion(currentQuestionData) ? (
              <>
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  Please select the assessment category you would like to take.
                </p>

                <div className="answer-options selection-options">
                  {currentQuestionData.options.map((option: string, index: number) => (
                    <button
                      key={index}
                      className={`option-button selection-button ${currentResponse?.answer === index ? 'selected' : ''}`}
                      onClick={() => saveCurrentResponse(index)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </>
            ) : isLikertQuestion(currentQuestionData) ? (
              <>
                <div className="category-indicator">
                  <span className="category-badge">
                    {getCategoryName(currentQuestionData.category)}
                  </span>
                </div>
                
                <h2 className="question-title">
                  {currentQuestionData.question}
                </h2>
                <p className="question-instruction">
                  {selectedCategory === 1 
                    ? 'Indicate how much you used this coping method when facing difficulties.'
                    : 'Please rate how well this statement describes you using the scale below.'}
                </p>

                <div className="answer-options">
                  {answerOptions.map((option) => (
                    <label key={option.value} className="answer-option">
                      <input
                        type="radio"
                        name={`question-${currentQuestion}`}
                        value={option.value}
                        checked={currentResponse?.answer === option.value}
                        onChange={(e) => saveCurrentResponse(parseInt(e.target.value))}
                      />
                      <span className="option-text">{option.label}</span>
                    </label>
                  ))}
                </div>
              </>
            ) : null}
          </div>

          {/* Hide navigation buttons on category selection page */}
          {selectedCategory !== null && (
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={() => {
                  if (currentQuestion === 1 && selectedCategory === null) {
                    navigate('/panel/learn/');
                    return;
                  }
                  if (currentQuestion === 1 && selectedCategory !== null) {
                    setSelectedCategory(null);
                    setCurrentQuestion(1);
                    return;
                  }
                  handleBack();
                }}
                disabled={false}
              >
                Back
              </button>

              <button
                className="btn-primary"
                onClick={handleNext}
                disabled={!currentResponse}
              >
                {currentQuestion === totalQuestions ? 'Complete Assessment' : 'Next'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReligioTraditioCulturalAssessmentFormPage;



